import { HttpErrorResponse } from '@angular/common/http';
import Swal from 'sweetalert2';
import {
  LOAN_APPLICATION_API_FALLBACK_MESSAGE,
  parseApiError,
} from './api-error-message.util';

/** LA-US-044: user-friendly API and validation errors for Loan Application flows. */
export function loanApplicationErrorMessage(
  error: unknown,
  options: {
    fieldCodes?: string[];
    fieldLabels?: Record<string, string>;
    fallback?: string;
  } = {},
): string {
  const explicitBodyMessage = readExplicitApiMessage(error);
  if (explicitBodyMessage !== null && explicitBodyMessage.length === 0) {
    return options.fallback ?? LOAN_APPLICATION_API_FALLBACK_MESSAGE;
  }

  const { message } = parseApiError(error, {
    fieldCodes: options.fieldCodes,
    fieldLabels: options.fieldLabels,
  });
  const trimmed = message.trim();
  if (trimmed.length > 0) {
    return trimmed;
  }
  return options.fallback ?? LOAN_APPLICATION_API_FALLBACK_MESSAGE;
}

function readExplicitApiMessage(error: unknown): string | null {
  if (!(error instanceof HttpErrorResponse)) {
    return null;
  }
  const body = error.error;
  if (!body || typeof body !== 'object' || !('message' in body)) {
    return null;
  }
  const message = (body as { message: unknown }).message;
  return typeof message === 'string' ? message.trim() : null;
}

export async function showLoanApplicationApiError(
  error: unknown,
  title = 'Unable to complete request',
  options: {
    fieldCodes?: string[];
    fieldLabels?: Record<string, string>;
    fallback?: string;
  } = {},
): Promise<void> {
  await Swal.fire({
    icon: 'error',
    title,
    text: loanApplicationErrorMessage(error, options),
  });
}

/** LA-US-045: confirm successful save / workflow actions. */
export async function showLoanApplicationSuccessToast(
  message: string,
): Promise<void> {
  await Swal.fire({
    toast: true,
    position: 'top-end',
    icon: 'success',
    title: message,
    showConfirmButton: false,
    timer: 3500,
  });
}

export async function showLoanApplicationWarning(
  title: string,
  text: string,
): Promise<void> {
  await Swal.fire({
    icon: 'warning',
    title,
    text,
  });
}

const SUCCESS_MESSAGE_SKIP = /^(ok|success)$/i;
const SUCCESS_MESSAGE_READ_PATTERN =
  /\b(retrieved|listed|loaded|fetched|returned)\b/i;

/** Read user-facing success copy from `{ success, message, data }` envelopes. */
export function readLoanApplicationApiSuccessMessage(body: unknown): string | null {
  if (!body || typeof body !== 'object') {
    return null;
  }
  const record = body as { success?: boolean; message?: unknown };
  if (record.success !== true) {
    return null;
  }
  const message =
    typeof record.message === 'string' ? record.message.trim() : '';
  if (!message || SUCCESS_MESSAGE_SKIP.test(message)) {
    return null;
  }
  if (SUCCESS_MESSAGE_READ_PATTERN.test(message)) {
    return null;
  }
  return message;
}
